// Dashboard FilterBar — date presets + slicer dropdowns.
// Extracted from `pages/Dashboards.jsx` (iter 41 refactor) — no behaviour change.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CalendarRange, Filter, RefreshCw, X } from "lucide-react";
import { recordFilterAdded, recordFilterRemoved } from "@/lib/playbookSignals";
import { useScopeFilter } from "@/lib/scope-filter-context";


// Default date range — applied to every dashboard if no date filter set yet.
// Configurable via per-tenant settings later; hardcoded global default for v1
// is "All time" because many data models hold entity-scoped dates that are
// months old (a 30-day window would hide everything).
export const DATE_PRESETS = [
    { key: "7d",   label: "Last 7 days",   days: 7 },
    { key: "30d",  label: "Last 30 days",  days: 30 },
    { key: "90d",  label: "Last 90 days",  days: 90 },
    { key: "ytd",  label: "Year-to-date",  days: null, ytd: true },
    { key: "all",  label: "All time",      days: null, all: true },
    { key: "custom", label: "Custom range", days: null, custom: true },
];


function _isoDay(d) {
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}


function _presetRange(preset) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (preset.all) return { from: "", to: "" };
    if (preset.ytd) {
        const jan1 = new Date(today.getFullYear(), 0, 1);
        return { from: _isoDay(jan1), to: _isoDay(today) };
    }
    if (preset.days) {
        const from = new Date(today);
        from.setDate(from.getDate() - preset.days + 1);
        return { from: _isoDay(from), to: _isoDay(today) };
    }
    return null;
}


export default function FilterBar({ dashboard, filters, setFilters, isLoading }) {
    const [options, setOptions] = useState([]);
    const [datePreset, setDatePreset] = useState("all");
    // iter-120 · forward the SA TopBar scope pill to `/filter-options` so
    // the dropdown values stay in sync with what the KPIs render. Non-SA
    // users pass `isSA=false` here and the params get ignored — the
    // backend pins the tenant scope from the JWT regardless. No
    // hardcoded role check on the frontend: the hook returns null /
    // false for non-SA users and the params are simply skipped.
    const scopeFilter = useScopeFilter();
    const scopeTenantId = scopeFilter?.isSA ? scopeFilter?.tenantId : undefined;
    const scopeOrgId = scopeFilter?.isSA ? scopeFilter?.orgId : undefined;
    useEffect(() => {
        if (!dashboard?.id) return;
        const params = {};
        if (scopeTenantId) params.scope_tenant_id = scopeTenantId;
        if (scopeOrgId) params.scope_org_id = scopeOrgId;
        api.get(`/dashboards/${dashboard.id}/filter-options`, { params })
           .then((r) => setOptions(r.data?.columns || []))
           .catch(() => setOptions([]));
    }, [dashboard?.id, scopeTenantId, scopeOrgId]);

    const dateOpt = options.find((o) => o.kind === "date") || { name: "date", label: "Date", kind: "date", _virtual: true };
    // iter-121.60 — Restrict chip visibility to filters DECLARED on the
    // template/dashboard. Before this fix the bar rendered every
    // categorical column auto-discovered from the data — so a clone
    // of a Prebuilt that the SA had configured with just `date + source`
    // showed up with the SA-only Tenant/Org/User chips, Channel, Status,
    // and any other enum-y dimension the LLM happened to surface. The
    // user spec: "filter configuration must remain unchanged. No new
    // filters should be introduced beyond those already configured."
    //
    // Behaviour:
    //   • If `dashboard.filters` is non-empty → only show chips whose
    //     `name` matches a declared filter's `field` (case-insensitive).
    //     Also keep system virtuals (`_tenant_id`, `_org_id`, `_user_id`)
    //     for SA scoping — but ONLY when SA explicitly declared them.
    //   • If `dashboard.filters` is empty → keep today's behaviour
    //     (auto-discover everything) for backwards-compat with
    //     prebuilts that never declared a filter set.
    const declared = Array.isArray(dashboard?.filters) ? dashboard.filters : [];
    const declaredFieldSet = new Set(
        declared.map((f) => (f?.field || f?.id || "").toLowerCase()).filter(Boolean),
    );
    const enumOpts = (declaredFieldSet.size > 0
        ? options.filter((o) =>
              o.kind !== "date"
              && declaredFieldSet.has((o.name || "").toLowerCase()),
          )
        : options.filter((o) => o.kind !== "date"));

    useEffect(() => {
        const cur = filters?.[dateOpt.name];
        if (!cur || (!cur.from && !cur.to)) {
            setDatePreset("all");
        } else {
            setDatePreset("custom");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard?.id, dateOpt.name]);

    const setOne = (col, v) => {
        const next = { ...filters };
        const wasSet = col in next;
        if (v === undefined || v === null || v === "" || v === "__all__") {
            delete next[col];
            // iter-119bl Phase 10 — emit filter_removed signal so SA can
            // see which prebuilt filter columns users routinely strip
            // off cloned canonical dashboards.
            if (wasSet) recordFilterRemoved(dashboard, col);
        } else {
            next[col] = v;
            // Only emit "added" the first time this column appears —
            // subsequent value changes on the same column are dedup'd
            // by the backend within a 5-minute window anyway, but no
            // need to chatter on every keystroke.
            if (!wasSet) recordFilterAdded(dashboard, col);
        }
        setFilters(next);
    };
    const applyDatePreset = (key) => {
        setDatePreset(key);
        const preset = DATE_PRESETS.find((p) => p.key === key);
        if (!preset || preset.custom) return;
        const r = _presetRange(preset);
        setOne(dateOpt.name, preset.all ? undefined : r);
    };
    const clear = () => {
        // iter-119bl Phase 10 — emit filter_removed for every currently-
        // active filter so the SA insights surface "everyone clears the
        // filters" as a coherent signal cluster.
        Object.keys(filters || {}).forEach((col) => recordFilterRemoved(dashboard, col));
        setFilters({});
        setDatePreset("all");
    };
    const filterCount = Object.keys(filters || {}).filter((k) => {
        const v = filters[k];
        return v && (typeof v !== "object" || v.from || v.to);
    }).length;

    return (
        <div className="border border-border rounded-sm bg-card mb-4" data-testid="dashboard-filter-bar">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/20 flex-wrap">
                <CalendarRange className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="eyebrow text-[10px]">Period</span>
                <div className="flex items-center gap-1 flex-wrap" data-testid="dashboard-filter-date-presets">
                    {DATE_PRESETS.filter((p) => !p.custom).map((p) => (
                        <button key={p.key}
                            onClick={() => applyDatePreset(p.key)}
                            className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                                datePreset === p.key
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border bg-card hover:border-foreground/40 text-muted-foreground"
                            }`}
                            data-testid={`dashboard-filter-preset-${p.key}`}>
                            {p.label}
                        </button>
                    ))}
                    <button
                        onClick={() => setDatePreset("custom")}
                        className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                            datePreset === "custom"
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border bg-card hover:border-foreground/40 text-muted-foreground"
                        }`}
                        data-testid="dashboard-filter-preset-custom">
                        Custom
                    </button>
                </div>
                {datePreset === "custom" && (
                    <div className="flex items-center gap-1" data-testid="dashboard-filter-date-custom">
                        <Input type="date"
                               className="h-7 w-36 rounded-sm text-xs bg-background"
                               value={filters[dateOpt.name]?.from || ""}
                               onChange={(e) => setOne(dateOpt.name, { ...(filters[dateOpt.name] || {}), from: e.target.value })}
                               data-testid="dashboard-filter-date-from" />
                        <span className="text-xs text-muted-foreground">→</span>
                        <Input type="date"
                               className="h-7 w-36 rounded-sm text-xs bg-background"
                               value={filters[dateOpt.name]?.to || ""}
                               onChange={(e) => setOne(dateOpt.name, { ...(filters[dateOpt.name] || {}), to: e.target.value })}
                               data-testid="dashboard-filter-date-to" />
                    </div>
                )}
                {filterCount > 0 && (
                    <Button variant="ghost" size="sm" className="ml-auto h-7 text-[11px] rounded-sm gap-1"
                            onClick={clear}
                            data-testid="dashboard-filter-clear">
                        <X className="h-3 w-3" /> Clear all ({filterCount})
                    </Button>
                )}
                {isLoading && (
                    <span className={`flex items-center gap-1.5 text-[11px] text-primary font-mono ${filterCount === 0 ? "ml-auto" : ""}`}
                          data-testid="dashboard-filter-loading">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Refreshing…
                    </span>
                )}
            </div>
            {enumOpts.length > 0 && (
                <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap" data-testid="dashboard-filter-slicers">
                    <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="eyebrow text-[10px]">Slice by</span>
                    {enumOpts.map((opt) => {
                        const valueMap = opt.value_map || null;
                        const current = filters[opt.name];
                        return (
                            <div key={opt.name} className="flex items-center gap-1.5">
                                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{opt.label || opt.name.replace(/^_/, "")}</span>
                                <Select value={current || "__all__"}
                                        onValueChange={(v) => setOne(opt.name, v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="h-7 w-44 rounded-sm text-xs bg-background" data-testid={`dashboard-filter-select-${opt.name}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        <SelectItem value="__all__" data-testid={`dashboard-filter-option-${opt.name}-all`}>All {(opt.label || opt.name.replace(/^_/, "")).toLowerCase()}</SelectItem>
                                        {valueMap
                                            ? valueMap.map((v) => <SelectItem key={v.value} value={v.value} data-testid={`dashboard-filter-option-${opt.name}-${v.value}`}>{v.label}</SelectItem>)
                                            : (opt.values || []).map((v) => <SelectItem key={v} value={v} data-testid={`dashboard-filter-option-${opt.name}-${v}`}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
